import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import it from "@/i18n/it.json";
import { Wordmark } from "@/components/Logo";

// Thank you page — Italian only per requirement, standalone (no header/footer)
export default function ThankYou() {
  const ty = it.thankyou;

  return (
    <div className="min-h-screen bg-[#F8F7F4] text-[#1A2421] flex flex-col">
      <header className="px-5 md:px-10 py-6 flex items-center justify-between">
        <Link to="/" data-testid="thankyou-header-home">
          <Wordmark />
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.24em] text-[#5C6B64] hover:text-[#C59B27]" data-testid="thankyou-header-link">
          <ArrowLeft className="w-3.5 h-3.5" /> {ty.backHome}
        </Link>
      </header>

      <main className="flex-1 flex items-center">
        <div className="mx-auto max-w-[900px] w-full px-5 md:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#C59B27]/40 bg-[#F7F2E6] px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C59B27] animate-pulse" />
              <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C59B27]" data-testid="thankyou-eyebrow">{ty.eyebrow}</span>
            </div>

            <h1
              className="mt-6 font-serif text-4xl sm:text-5xl md:text-6xl text-[#1C352D] leading-[1.02] tracking-tight max-w-[16ch]"
              data-testid="thankyou-title"
            >
              {ty.title}
            </h1>

            <p className="mt-6 max-w-[60ch] text-[15.5px] leading-relaxed text-[#4A5D55]" data-testid="thankyou-subtitle">
              {ty.subtitle}
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8">
              <div className="md:col-span-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C59B27]">{ty.next}</div>
              </div>
              <ul className="md:col-span-8 space-y-4">
                {ty.bullets.map((b, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.08 }}
                    className="flex items-start gap-3 border-t border-[#1C352D]/12 pt-4"
                  >
                    <span className="mt-1 inline-flex w-5 h-5 shrink-0 rounded-full bg-[#1C352D] items-center justify-center">
                      <Check className="w-3 h-3 text-[#D4AF37]" />
                    </span>
                    <span className="text-[14px] text-[#1A2421] leading-relaxed">{b}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="mt-14">
              <Link
                to="/"
                className="group inline-flex items-center gap-2 rounded-full bg-[#1C352D] pl-5 pr-2 py-2 text-[13px] font-medium text-[#F8F7F4] hover:bg-[#2D4A3E] transition-colors"
                data-testid="thankyou-back-home"
              >
                {ty.backHome}
                <span className="ml-1 inline-flex w-8 h-8 rounded-full bg-[#D4AF37] items-center justify-center text-[#1C352D] group-hover:-translate-x-0.5 transition-transform">
                  <ArrowLeft className="w-3.5 h-3.5" />
                </span>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="px-5 md:px-10 py-8 border-t border-[#1C352D]/10 text-[11px] font-mono uppercase tracking-[0.24em] text-[#5C6B64] flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <span>© 2026 Ecominerali S.r.l. · Milano</span>
        <span>P.IVA IT09876540152 · REA MI-2549811</span>
      </footer>
    </div>
  );
}
